import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import DefaultTableEmptyState from 'components/UI/V3/Table/EmptyState/DefaultTableEmptyState';
import { TableEmptyState } from 'components/UI/V3/Table/EmptyState/TableEmptyState';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { Table } from 'components/UI/V3/Table/Table';
import { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { IdogcamCamera } from '../model/IdogcamModel';
import IdogcamCamerasTableRow from './IdogcamCamerasTableRow';
import styles from './IdogcamCamerasTable.module.scss';

interface IdogcamCamerasTableProps {
    cameras: Array<IdogcamCamera>;
    locationName?: string;
    page: number;
    pageSize: number;
    totalPages: number;
    isSearching: boolean;
    isLoading?: boolean;
    className?: string;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onConnectionSettings: () => void;
    onEdit?: (camera: IdogcamCamera) => void;
    onView?: (camera: IdogcamCamera) => void;
}

export const IdogcamCamerasTable: FunctionComponent<IdogcamCamerasTableProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    if (props.cameras.length === 0) {
        return (
            <Table
                className={clsx(styles.table, props.className)}
                fullHeight={true}
            >
                <TableBody className={styles.emptyBody}>
                    {props.isSearching ? (
                        <DefaultTableEmptyState />
                    ) : (
                        // Connected but no cameras: either the account has none yet or the
                        // credentials point to the wrong kennel, so offer a way to check them.
                        <TableEmptyState>
                            <div className={styles.emptyContent}>
                                <p className={styles.emptyTitle}>
                                    {props.locationName
                                        ? `No cameras found in ${props.locationName}`
                                        : 'No cameras found'}
                                </p>
                                <p className={styles.emptyDescription}>
                                    If you expected cameras here, check your Kennel ID and ERP Code.
                                </p>
                                <Button
                                    label="Connection settings"
                                    variant="primary"
                                    className={styles.emptyButton}
                                    onClick={props.onConnectionSettings}
                                />
                            </div>
                        </TableEmptyState>
                    )}
                </TableBody>
            </Table>
        );
    }

    return (
        <Table
            pagination={{
                page: props.page,
                totalPages: props.totalPages,
                size: props.pageSize,
                isLoading: props.isLoading
            }}
            onPageChange={props.onPageChange}
            onSizeChange={props.onPageSizeChange}
            className={props.className}
            fullHeight={true}
        >
            {!isMobile && (
                <TableHeader>
                    <TableHeaderCell>Camera ID</TableHeaderCell>
                    <TableHeaderCell>Camera Name</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell />
                </TableHeader>
            )}
            <TableBody>
                {props.cameras.map(camera => (
                    <IdogcamCamerasTableRow
                        key={camera.id}
                        camera={camera}
                        onEdit={props.onEdit}
                        onView={props.onView}
                    />
                ))}
            </TableBody>
        </Table>
    );
};

export default IdogcamCamerasTable;
